import React, { useState, useCallback } from 'react';
import { useMap } from '../map/MapContext';
import { useOgcServices } from '../services/OgcServicesContext';
import { getAllAirspaceStyles, type AirspaceStyleDef } from '../map/aviation-sources';
import { buildAirspaceFilter, type AirspaceFilterCriteria } from '../services/cql2-aviation-presets';
import type { FeatureCollection } from 'geojson';

interface AirspaceToggle extends AirspaceStyleDef {
  visible: boolean;
}

export const AirspacePanel: React.FC = () => {
  const { map } = useMap();
  const { featuresClient } = useOgcServices();
  const [airspaces, setAirspaces] = useState<AirspaceToggle[]>(() =>
    getAllAirspaceStyles().map((s) => ({
      ...s,
      visible: !['airspace-class-e', 'airspace-warning', 'airspace-alert'].includes(s.id),
    })),
  );

  // ─── CQL filter state ──────────────────────────────────────────────────────
  const [showCqlFilters, setShowCqlFilters] = useState(false);
  const [filterMaxFloor, setFilterMaxFloor] = useState('');
  const [filterMinCeiling, setFilterMinCeiling] = useState('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  const [filterSearchText, setFilterSearchText] = useState('');
  const [filterAuthority, setFilterAuthority] = useState('');
  const [cqlResults, setCqlResults] = useState<FeatureCollection | null>(null);
  const [cqlLoading, setCqlLoading] = useState(false);
  const [cqlPreview, setCqlPreview] = useState('');

  const buildCurrentCql = useCallback((): string => {
    const criteria: AirspaceFilterCriteria = {};
    if (filterMaxFloor) criteria.maxFloorFt = parseInt(filterMaxFloor, 10) || undefined;
    if (filterMinCeiling) criteria.minCeilingFt = parseInt(filterMinCeiling, 10) || undefined;
    if (filterActiveOnly) criteria.activeNow = true;
    if (filterSearchText.trim()) criteria.textSearch = filterSearchText.trim();
    if (filterAuthority.trim()) criteria.authority = filterAuthority.trim();
    return buildAirspaceFilter(criteria);
  }, [filterMaxFloor, filterMinCeiling, filterActiveOnly, filterSearchText, filterAuthority]);

  const applyCqlFilter = useCallback(async () => {
    const cql = buildCurrentCql();
    if (!cql) return;
    setCqlPreview(cql);
    setCqlLoading(true);
    try {
      let bbox: [number, number, number, number] | undefined;
      if (map) {
        const b = map.getBounds();
        bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
      }
      const fc = bbox
        ? await featuresClient.searchByBboxWithFilter('airspaces', bbox, cql, 500)
        : await featuresClient.getFeatures('airspaces', { filter: cql, limit: 500 });
      setCqlResults(fc);

      // Add filtered results as a highlighted GeoJSON layer
      if (map) {
        const srcId = 'airspace-cql-results';
        if (map.getSource(srcId)) {
          (map.getSource(srcId) as any).setData(fc);
        } else {
          map.addSource(srcId, { type: 'geojson', data: fc });
          map.addLayer({
            id: `${srcId}-fill`, type: 'fill', source: srcId,
            paint: { 'fill-color': '#F59E0B', 'fill-opacity': 0.2 },
          });
          map.addLayer({
            id: `${srcId}-line`, type: 'line', source: srcId,
            paint: { 'line-color': '#F59E0B', 'line-width': 2.5 },
          });
        }
      }
    } catch (err) {
      console.error('Airspace CQL filter failed:', err);
    } finally {
      setCqlLoading(false);
    }
  }, [map, featuresClient, buildCurrentCql]);

  const clearCqlFilter = useCallback(() => {
    setCqlResults(null);
    setCqlPreview('');
    if (map) {
      try { map.removeLayer('airspace-cql-results-fill'); } catch { /* ok */ }
      try { map.removeLayer('airspace-cql-results-line'); } catch { /* ok */ }
      try { map.removeSource('airspace-cql-results'); } catch { /* ok */ }
    }
  }, [map]);

  const toggleAirspace = useCallback((id: string) => {
    setAirspaces((prev) => prev.map((a) => {
      if (a.id !== id) return a;
      const next = { ...a, visible: !a.visible };
      if (map) {
        const vis = next.visible ? 'visible' : 'none';
        try { map.setLayoutProperty(`${id}-fill`, 'visibility', vis); } catch { /* */ }
        try { map.setLayoutProperty(`${id}-line`, 'visibility', vis); } catch { /* */ }
      }
      return next;
    }));
  }, [map]);

  const formatLabel = (id: string): string => {
    return id
      .replace('airspace-', '')
      .replace('class-', 'Class ')
      .replace(/^(\w)/, (_, c) => c.toUpperCase())
      .replace('moa', 'MOA')
      .replace('tfr', 'TFR');
  };

  return (
    <div>
      <div className="panel-section">
        <div className="panel-section-title">Airspace Visibility</div>
        {airspaces.map((a) => (
          <div key={a.id} className="list-item">
            <span
              style={{
                width: 14, height: 14, borderRadius: 3,
                background: a.fillColor, border: `2px solid ${a.lineColor}`,
                opacity: a.visible ? 1 : 0.3,
                flexShrink: 0,
              }}
            />
            <label className="toggle" style={{ flex: 1 }}>
              <span
                className={`toggle-box ${a.visible ? 'checked' : ''}`}
                onClick={() => toggleAirspace(a.id)}
              />
              <span style={{ fontSize: 13 }}>{formatLabel(a.id)}</span>
            </label>
          </div>
        ))}
      </div>

      {/* CQL Altitude / Schedule Filters */}
      <div className="panel-section">
        <div className="collapsible-header" onClick={() => setShowCqlFilters((p) => !p)}>
          <span className={`chevron ${showCqlFilters ? 'open' : ''}`}>▶</span>
          <span className="panel-section-title" style={{ margin: 0 }}>
            CQL Data Filters
            {cqlResults && <span style={{ color: 'var(--accent-light)', fontSize: 11 }}> ({cqlResults.features.length} results)</span>}
          </span>
        </div>
        {showCqlFilters && (
          <div style={{ paddingTop: 6 }}>
            {/* Altitude filters */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Altitude Range (ft MSL)</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="input" type="number" placeholder="Max floor" value={filterMaxFloor}
                  onChange={(e) => setFilterMaxFloor(e.target.value)} style={{ width: 100 }} />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>to</span>
                <input className="input" type="number" placeholder="Min ceiling" value={filterMinCeiling}
                  onChange={(e) => setFilterMinCeiling(e.target.value)} style={{ width: 100 }} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                Show airspace with floor ≤ max and ceiling ≥ min
              </div>
            </div>
            {/* Active now */}
            <div style={{ marginBottom: 8 }}>
              <label className="toggle">
                <span className={`toggle-box ${filterActiveOnly ? 'checked' : ''}`} onClick={() => setFilterActiveOnly(!filterActiveOnly)} />
                <span style={{ fontSize: 12 }}>Active / Effective Now</span>
              </label>
            </div>
            {/* Text search */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Name / Designator</div>
              <input className="input" placeholder="e.g. VANCE MOA" value={filterSearchText}
                onChange={(e) => setFilterSearchText(e.target.value)} />
            </div>
            {/* Authority */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Controlling Authority</div>
              <input className="input" placeholder="e.g. ZFW, ZLA" value={filterAuthority}
                onChange={(e) => setFilterAuthority(e.target.value)} />
            </div>
            {/* Apply / Clear */}
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary" onClick={applyCqlFilter} disabled={cqlLoading} style={{ flex: 1 }}>
                {cqlLoading ? 'Filtering…' : '🔎 Apply CQL Filter'}
              </button>
              {cqlResults && (
                <button className="btn btn-secondary" onClick={clearCqlFilter}>Clear</button>
              )}
            </div>
            {/* CQL preview */}
            {cqlPreview && (
              <div style={{ background: 'var(--bg-input)', borderRadius: 4, padding: 6, marginTop: 6 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>CQL2 Filter:</div>
                <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent-light)', wordBreak: 'break-all' }}>
                  {cqlPreview}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick presets */}
      <div className="panel-section">
        <div className="panel-section-title">Quick Presets</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="chip" onClick={() => {
            setAirspaces((prev) => prev.map((a) => ({ ...a, visible: true })));
          }}>All On</button>
          <button className="chip" onClick={() => {
            setAirspaces((prev) => prev.map((a) => ({ ...a, visible: false })));
          }}>All Off</button>
          <button className="chip" onClick={() => {
            setAirspaces((prev) => prev.map((a) => ({
              ...a,
              visible: ['airspace-class-b', 'airspace-class-c', 'airspace-class-d',
                'airspace-moa', 'airspace-restricted', 'airspace-prohibited', 'airspace-tfr'].includes(a.id),
            })));
          }}>VFR Defaults</button>
          <button className="chip" onClick={() => {
            setAirspaces((prev) => prev.map((a) => ({
              ...a,
              visible: ['airspace-class-b', 'airspace-class-c', 'airspace-class-d',
                'airspace-class-e', 'airspace-restricted', 'airspace-prohibited', 'airspace-tfr'].includes(a.id),
            })));
          }}>IFR Defaults</button>
        </div>
      </div>
    </div>
  );
};
